import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import RemoveIcon from '@mui/icons-material/Remove';
import AuthContext from '../../context/AuthContext';
import { billingService, whatsappAccountService, planService, invoiceService } from '../../services/api';
import { daysOverdue, overdueLabel, OVERDUE_COLOR } from '../../utils/overdue';
import AppLayout from '../layout/AppLayout';
import InvoiceDialog from './InvoiceDialog';
import InvoiceDetailDialog from './InvoiceDetailDialog';
import { INK, MUTED, RULE, Section } from '../analytics/analyticsUi';

const GREEN = '#1BAA52';
const ACTIVE_BG = 'rgba(37, 211, 102, 0.08)';
const DAY_MS = 24 * 60 * 60 * 1000;

const SUBSCRIPTION_STATUS = {
  active: { label: 'Active', color: GREEN },
  pending: { label: 'Awaiting payment', color: '#C77700' },
  cancelled: { label: 'Cancelled', color: '#C2410C' },
  expired: { label: 'Expired', color: MUTED }
};

const CYCLES = {
  monthly: { label: 'Monthly', per: 'month', months: 1 },
  quarterly: { label: 'Quarterly', per: 'quarter', months: 3 },
  annually: { label: 'Annually', per: 'year', months: 12 }
};

const PAYMENT_METHODS = {
  credit_card: 'Credit card',
  debit_card: 'Debit card',
  net_banking: 'Net banking',
  upi: 'UPI',
  wallet: 'Wallet',
  offline: 'Offline payment'
};

const FEATURES = [
  { key: 'api', label: 'API access' },
  { key: 'sharedInbox', label: 'Shared inbox' },
  { key: 'sentimentAnalysis', label: 'Sentiment analysis' },
  { key: 'bulkMessaging', label: 'Bulk messaging' }
];

const formatMoney = (amount, currency = 'INR') =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR', maximumFractionDigits: 2 }).format(amount || 0);
const formatDay = (value) =>
  value ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '–';
const toInputDate = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};
const addCycle = (startValue, cycle) => {
  const [y, m, d] = startValue.split('-').map(Number);
  const end = new Date(y, m - 1, d);
  end.setMonth(end.getMonth() + (CYCLES[cycle]?.months || 1));
  return toInputDate(end);
};

const statusMeta = (status) => SUBSCRIPTION_STATUS[status] || { label: status, color: MUTED };

const StatusDot = ({ status }) => {
  const meta = statusMeta(status);
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: meta.color }} />
      <Typography variant="body2" sx={{ color: INK }}>{meta.label}</Typography>
    </Box>
  );
};

const accountLabel = (account, accounts) => {
  const found = typeof account === 'object' && account ? account : accounts.find((a) => a._id === account);
  return found ? { name: found.businessName, phone: found.phoneNumber } : { name: 'Unknown account', phone: '' };
};

// ---------------------------------------------------------------------------
// Current plan
// ---------------------------------------------------------------------------
const CurrentPlan = ({ billing, planName, accounts, onInvoices, onCancel, onChoosePlan }) => {
  if (!billing) {
    return (
      <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 4 }, mb: 3, borderColor: RULE, borderRadius: 2 }}>
        <Typography component="h2" sx={{ fontSize: '1.25rem', fontWeight: 700, color: INK }}>You don't have a plan yet</Typography>
        <Typography variant="body2" sx={{ color: MUTED, mt: 0.75, mb: 2.5, maxWidth: '60ch' }}>
          Compare the plans below and choose the one that fits how many messages you send each month.
        </Typography>
        <Button variant="contained" onClick={onChoosePlan} sx={{ textTransform: 'none' }}>Choose a plan</Button>
      </Paper>
    );
  }

  const start = billing.startDate ? new Date(billing.startDate).getTime() : null;
  const end = billing.endDate ? new Date(billing.endDate).getTime() : null;
  const now = Date.now();
  const progress = start && end && end > start ? Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100)) : 0;
  const daysLeft = end ? Math.max(0, Math.ceil((end - now) / DAY_MS)) : null;
  const account = accountLabel(billing.whatsappAccount, accounts);
  const cycle = CYCLES[billing.billingCycle] || CYCLES.monthly;
  const isPending = billing.status === 'pending';
  const isActive = billing.status === 'active';

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 3,
        borderColor: RULE,
        borderRadius: 2,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' },
        overflow: 'hidden'
      }}
    >
      <Box sx={{ p: { xs: 2.5, sm: 3.5 }, borderRight: { md: `1px solid ${RULE}` }, borderBottom: { xs: `1px solid ${RULE}`, md: 'none' } }}>
        <Typography variant="body2" sx={{ color: MUTED }}>Current plan</Typography>
        <Typography sx={{ fontSize: { xs: '2.25rem', md: '2.75rem' }, fontWeight: 700, letterSpacing: '-0.03em', color: INK, lineHeight: 1.1, mt: 0.5 }}>
          {planName}
        </Typography>
        <Typography sx={{ color: INK, mt: 1, fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(billing.amount, billing.currency)}
          <Box component="span" sx={{ color: MUTED }}> per {cycle.per}</Box>
        </Typography>
        <Box sx={{ mt: 1.5 }}>
          <StatusDot status={billing.status} />
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 3 }}>
          <Button variant="outlined" onClick={() => onInvoices(billing)} sx={{ textTransform: 'none' }}>View invoices</Button>
          {billing.status !== 'cancelled' && (
            <Button color="error" onClick={() => onCancel(billing)} sx={{ textTransform: 'none' }}>Cancel subscription</Button>
          )}
        </Box>
      </Box>

      <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        {isPending && (
          <Alert severity="warning" variant="outlined" sx={{ mb: 2.5 }}>
            This subscription starts once the payment is confirmed.
          </Alert>
        )}
        {end && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, mb: 1 }}>
              <Typography sx={{ fontWeight: 600, color: INK }}>
                {daysLeft === 0 ? 'Ends today' : `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`}
              </Typography>
              <Typography variant="body2" sx={{ color: MUTED }}>
                {isActive && billing.autoRenew ? 'Renews' : 'Ends'} on {formatDay(billing.endDate)}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              aria-label={`${Math.round(progress)}% of the billing period used`}
              sx={{ height: 8, borderRadius: 4, bgcolor: RULE, '& .MuiLinearProgress-bar': { bgcolor: 'primary.main', borderRadius: 4 } }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
              <Typography variant="caption" sx={{ color: MUTED }}>{formatDay(billing.startDate)}</Typography>
              <Typography variant="caption" sx={{ color: MUTED }}>{formatDay(billing.endDate)}</Typography>
            </Box>
          </Box>
        )}

        {[
          { label: 'WhatsApp account', value: `${account.name}${account.phone ? `, ${account.phone}` : ''}` },
          { label: 'Billing cycle', value: cycle.label },
          { label: 'Auto-renew', value: billing.autoRenew ? 'On' : 'Off' },
          { label: 'Payment method', value: PAYMENT_METHODS[billing.paymentMethod] || billing.paymentMethod || '–' }
        ].map((row) => (
          <Box key={row.label} sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 2, py: 1, borderTop: `1px solid ${RULE}` }}>
            <Typography variant="body2" sx={{ color: MUTED }}>{row.label}</Typography>
            <Typography variant="body2" sx={{ color: INK, overflowWrap: 'anywhere' }}>{row.value}</Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Plan comparison: plans as columns, features as rows
// ---------------------------------------------------------------------------
const PlanComparison = ({ plans, currentPlanKey, onChoose, canSubscribe }) => {
  const rows = [
    { label: 'Message limit', render: (plan) => (plan.messageRateLimit || 0).toLocaleString('en-IN') },
    ...FEATURES.map((feature) => ({
      label: feature.label,
      render: (plan) => (plan.features?.[feature.key]
        ? <CheckIcon aria-label="Included" sx={{ fontSize: 18, color: GREEN }} />
        : <RemoveIcon aria-label="Not included" sx={{ fontSize: 18, color: '#B8BFCB' }} />)
    })),
    { label: 'Team members', render: (plan) => plan.maxUsers ?? '–' },
    { label: 'File storage', render: (plan) => (plan.fileStorageLimit != null ? `${plan.fileStorageLimit} GB` : '–') }
  ];

  const cellSx = (plan) => ({
    borderColor: RULE,
    textAlign: 'center',
    bgcolor: plan.name.toLowerCase() === currentPlanKey ? ACTIVE_BG : 'transparent'
  });

  return (
    <Section title="Plans" description="Compare what each plan includes.">
      {!plans.length ? (
        <Typography variant="body2" sx={{ color: MUTED }}>No plans are available right now.</Typography>
      ) : (
        <TableContainer sx={{ mx: { xs: -2, sm: -3 }, width: 'auto' }}>
          <Table sx={{ minWidth: 160 + plans.length * 150 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ borderColor: RULE, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1, pl: { xs: 2, sm: 3 } }} />
                {plans.map((plan) => {
                  const isCurrent = plan.name.toLowerCase() === currentPlanKey;
                  return (
                    <TableCell key={plan._id} sx={{ ...cellSx(plan), verticalAlign: 'top', pt: 2 }}>
                      <Typography variant="body2" sx={{ color: isCurrent ? '#0D7332' : MUTED, fontWeight: 600, minHeight: 20 }}>
                        {isCurrent ? 'Your plan' : ''}
                      </Typography>
                      <Typography sx={{ fontWeight: 700, color: INK, fontSize: '1.05rem' }}>{plan.name}</Typography>
                      <Typography sx={{ color: INK, fontVariantNumeric: 'tabular-nums', fontSize: '1.375rem', fontWeight: 600, letterSpacing: '-0.02em', mt: 0.5 }}>
                        {formatMoney(plan.price, plan.currency).replace(/\.00$/, '')}
                      </Typography>
                      {plan.description && (
                        <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 0.5 }}>{plan.description}</Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.label}>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{ borderColor: RULE, color: MUTED, position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1, pl: { xs: 2, sm: 3 }, whiteSpace: 'nowrap' }}
                  >
                    {row.label}
                  </TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan._id} sx={{ ...cellSx(plan), color: INK, fontVariantNumeric: 'tabular-nums' }}>
                      {row.render(plan)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ borderBottom: 'none', position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }} />
                {plans.map((plan) => {
                  const isCurrent = plan.name.toLowerCase() === currentPlanKey;
                  return (
                    <TableCell key={plan._id} sx={{ ...cellSx(plan), borderBottom: 'none', py: 2 }}>
                      <Button
                        size="small"
                        variant={isCurrent ? 'text' : 'outlined'}
                        disabled={isCurrent || !canSubscribe}
                        onClick={() => onChoose(plan)}
                        sx={{ textTransform: 'none' }}
                      >
                        {isCurrent ? 'Current plan' : `Choose ${plan.name}`}
                      </Button>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const emptyForm = {
  whatsappAccount: '',
  plan: '',
  amount: 0,
  currency: 'INR',
  billingCycle: 'monthly',
  startDate: '',
  endDate: '',
  paymentMethod: 'credit_card',
  autoRenew: true
};

// ---------------------------------------------------------------------------
// Invoices: monthly bills (plan fee + WhatsApp messages + GST) and subscription invoices
// ---------------------------------------------------------------------------
const INVOICE_STATUS = {
  pending: { label: 'Awaiting payment', color: '#C77700' },
  paid: { label: 'Paid', color: GREEN },
  failed: { label: 'Payment failed', color: '#C2410C' },
  cancelled: { label: 'Cancelled', color: MUTED },
  draft: { label: 'Draft', color: MUTED }
};

const InvoicesSection = ({ invoices, loading, showCustomer }) => {
  const overdueList = invoices.filter((inv) => daysOverdue(inv) > 0);
  const [viewing, setViewing] = useState(null);
  const totalOf = (inv) => inv.total ?? (inv.amount + (inv.taxAmount || 0) - (inv.discountAmount || 0));
  const outstanding = invoices.filter((inv) => inv.status === 'pending');
  return (
    <Section
      title="Invoices"
      description={outstanding.length
        ? `${outstanding.length} awaiting payment, ${formatMoney(outstanding.reduce((sum, inv) => sum + totalOf(inv), 0))} in total.`
        : 'Monthly bills for your plan and WhatsApp messages, including GST.'}
    >
      {!showCustomer && overdueList.length > 0 && (
        <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
          {overdueList.length === 1 ? 'One invoice is' : `${overdueList.length} invoices are`} overdue,
          {' '}{formatMoney(overdueList.reduce((sum, inv) => sum + totalOf(inv), 0))} in total. Please pay at the earliest,
          or contact us if you have already paid.
        </Alert>
      )}
      {loading ? (
        <Skeleton variant="rounded" height={120} />
      ) : !invoices.length ? (
        <Typography variant="body2" sx={{ color: MUTED }}>No invoices yet. Your first bill is created after the end of the month.</Typography>
      ) : (
        <TableContainer sx={{ mx: { xs: -2, sm: -3 }, width: 'auto' }}>
          <Table size="small" sx={{ minWidth: showCustomer ? 820 : 680 }}>
            <TableHead>
              <TableRow sx={{ '& th': { color: MUTED, fontWeight: 500, borderColor: RULE } }}>
                <TableCell sx={{ pl: { xs: 2, sm: 3 } }}>Invoice</TableCell>
                {showCustomer && <TableCell>Customer</TableCell>}
                <TableCell>For</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right" sx={{ pr: { xs: 2, sm: 3 } }}>
                  <Box component="span" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Download</Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((inv) => {
                const status = INVOICE_STATUS[inv.status] || { label: inv.status, color: MUTED };
                const overdue = daysOverdue(inv);
                return (
                  <TableRow
                    key={inv._id}
                    hover
                    onClick={(e) => { if (!e.target.closest('button, a')) setViewing(inv); }}
                    sx={{ cursor: 'pointer', '& td': { borderColor: RULE, py: 1.25, fontVariantNumeric: 'tabular-nums' } }}
                  >
                    <TableCell sx={{ pl: { xs: 2, sm: 3 } }}>
                      <Typography variant="body2" sx={{ color: INK, fontWeight: 600 }}>{inv.invoiceNumber}</Typography>
                      <Typography variant="caption" sx={{ color: MUTED }}>Issued {formatDay(inv.createdAt)}</Typography>
                    </TableCell>
                    {showCustomer && (
                      <TableCell>
                        <Typography variant="body2" sx={{ color: INK }}>{inv.user?.company || inv.user?.name || '–'}</Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" sx={{ color: INK }}>
                        {inv.billingMonth
                          ? new Date(`${inv.billingMonth}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
                          : `${formatDay(inv.billingPeriodStart)} to ${formatDay(inv.billingPeriodEnd)}`}
                      </Typography>
                      <Typography variant="caption" sx={{ color: MUTED }}>
                        {inv.whatsappAccount ? `${inv.whatsappAccount.verifiedName || inv.whatsappAccount.businessName}, ${inv.whatsappAccount.phoneNumber}` : 'Subscription'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ color: INK, fontWeight: 600 }}>{formatMoney(totalOf(inv), inv.currency)}</Typography>
                      {inv.taxAmount > 0 && <Typography variant="caption" sx={{ color: MUTED }}>incl. GST {formatMoney(inv.taxAmount, inv.currency)}</Typography>}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: overdue ? OVERDUE_COLOR : status.color }} />
                        <Typography variant="body2" sx={{ color: INK }}>{status.label}</Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: overdue ? OVERDUE_COLOR : MUTED, fontWeight: overdue ? 600 : 400, display: 'block' }}>
                        {inv.status === 'paid' ? `Paid ${formatDay(inv.paidDate)}` : overdue ? `${overdueLabel(overdue)} (due ${formatDay(inv.dueDate)})` : `Due ${formatDay(inv.dueDate)}`}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ pr: { xs: 2, sm: 3 } }}>
                      <Button size="small" onClick={() => setViewing(inv)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                        View
                      </Button>
                      <Button
                        size="small"
                        component="a"
                        href={billingService.generatePdfUrl(inv._id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textTransform: 'none' }}
                      >
                        PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <InvoiceDetailDialog invoice={viewing} open={!!viewing} onClose={() => setViewing(null)} />
    </Section>
  );
};

const Billing = () => {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';
  const userId = user?._id || user?.id;

  const [billings, setBillings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [invoiceTarget, setInvoiceTarget] = useState(null);

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchInvoices = async () => {
    try {
      setInvoicesLoading(true);
      const response = await invoiceService.list({ limit: 200 });
      setInvoices(response.data || []);
    } catch (err) {
      setInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const [plansResponse, accountsResponse, billingsResponse] = await Promise.all([
        planService.getPlans(),
        whatsappAccountService.getAccounts(1, 1000),
        billingService.getBillings()
      ]);
      setPlans((plansResponse.data || []).filter((p) => p.isActive !== false).sort((a, b) => (a.price || 0) - (b.price || 0)));
      setAccounts(accountsResponse.data || []);
      setBillings(billingsResponse.data || []);
    } catch (err) {
      setLoadError('Billing details could not be loaded. Refresh the page to try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchInvoices();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Admins receive every customer's records; the plan panel shows the admin's own
  const ownBillings = useMemo(() => billings.filter((b) => {
    if (!isAdmin) return true;
    const ownerId = typeof b.user === 'object' ? b.user?._id : b.user;
    return String(ownerId) === String(userId);
  }), [billings, isAdmin, userId]);

  const ownAccounts = useMemo(() => accounts.filter((a) => {
    if (!isAdmin) return true;
    const ownerId = typeof a.user === 'object' ? a.user?._id : a.user;
    return String(ownerId) === String(userId);
  }), [accounts, isAdmin, userId]);

  const currentBilling = useMemo(() => {
    const byNewest = [...ownBillings].sort((a, b) => new Date(b.createdAt || b.startDate || 0) - new Date(a.createdAt || a.startDate || 0));
    return byNewest.find((b) => b.status === 'active') || byNewest.find((b) => b.status === 'pending') || null;
  }, [ownBillings]);

  const history = useMemo(
    () => [...billings].sort((a, b) => new Date(b.startDate || b.createdAt || 0) - new Date(a.startDate || a.createdAt || 0)),
    [billings]
  );

  const planDisplayName = (key) => plans.find((p) => p.name.toLowerCase() === String(key).toLowerCase())?.name
    || (key ? key.charAt(0).toUpperCase() + key.slice(1) : '–');

  const canSubscribe = ownAccounts.length > 0 && plans.length > 0;

  // ---- Subscribe dialog
  const openSubscribe = (plan) => {
    const chosen = plan || plans[0];
    const start = toInputDate(new Date());
    setFormData({
      ...emptyForm,
      whatsappAccount: ownAccounts[0]?._id || '',
      plan: chosen ? chosen.name.toLowerCase() : '',
      amount: chosen?.price || 0,
      currency: chosen?.currency || 'INR',
      startDate: start,
      endDate: addCycle(start, 'monthly')
    });
    setSubscribeOpen(true);
  };

  const updateForm = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'plan') {
        const plan = plans.find((p) => p.name.toLowerCase() === value);
        if (plan) {
          next.amount = plan.price;
          next.currency = plan.currency || 'INR';
        }
      }
      if ((field === 'billingCycle' || field === 'startDate') && next.startDate) {
        next.endDate = addCycle(next.startDate, next.billingCycle);
      }
      return next;
    });
  };

  const submitSubscription = async () => {
    try {
      setSaving(true);
      await billingService.createBilling({ ...formData, status: 'pending' });
      setSuccess('Subscription requested. It becomes active once the payment is confirmed.');
      setSubscribeOpen(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'The subscription could not be created. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    try {
      setCancelling(true);
      await billingService.cancelSubscription(cancelTarget._id);
      setSuccess('Subscription cancelled. It won\'t renew.');
      setCancelTarget(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'The subscription could not be cancelled. Try again.');
    } finally {
      setCancelling(false);
    }
  };

  const selectedPlan = plans.find((p) => p.name.toLowerCase() === formData.plan);

  return (
    <AppLayout>
      <Container maxWidth="lg" sx={{ pb: 6 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2, mb: 3 }}>
          <Box>
            <Typography component="h1" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 700, letterSpacing: '-0.025em', color: INK, lineHeight: 1.15 }}>
              Billing
            </Typography>
            <Typography variant="body2" sx={{ color: MUTED, mt: 0.75 }}>
              Your plan, subscription history and invoices.
            </Typography>
          </Box>
          <Button variant="contained" onClick={() => openSubscribe()} disabled={loading || !canSubscribe} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Subscribe to a plan
          </Button>
        </Box>

        {!loading && !ownAccounts.length && !loadError && (
          <Alert severity="info" variant="outlined" sx={{ mb: 3, borderColor: RULE, color: INK }}>
            Plans are billed per WhatsApp account.{' '}
            <Box component={RouterLink} to="/whatsapp-onboarding" sx={{ color: '#0D7332', fontWeight: 600 }}>Connect an account</Box> to subscribe.
          </Alert>
        )}

        {loadError && <Alert severity="error" sx={{ mb: 3 }}>{loadError}</Alert>}

        {loading ? (
          <Box aria-busy="true">
            <Skeleton variant="rounded" height={240} sx={{ mb: 3 }} />
            <Skeleton variant="rounded" height={320} sx={{ mb: 3 }} />
            <Skeleton variant="rounded" height={200} />
          </Box>
        ) : (
          <>
            <CurrentPlan
              billing={currentBilling}
              planName={currentBilling ? planDisplayName(currentBilling.plan) : ''}
              accounts={accounts}
              onInvoices={setInvoiceTarget}
              onCancel={setCancelTarget}
              onChoosePlan={() => openSubscribe()}
            />

            <PlanComparison
              plans={plans}
              currentPlanKey={currentBilling?.status === 'active' ? String(currentBilling.plan).toLowerCase() : null}
              onChoose={openSubscribe}
              canSubscribe={canSubscribe}
            />

            <InvoicesSection invoices={invoices} loading={invoicesLoading} showCustomer={isAdmin} />

            <Section
              title={isAdmin ? 'All subscriptions' : 'Subscription history'}
              description={isAdmin ? 'Every customer\'s subscriptions, newest first.' : undefined}
            >
              {!history.length ? (
                <Typography variant="body2" sx={{ color: MUTED }}>No subscriptions yet.</Typography>
              ) : (
                <TableContainer sx={{ mx: { xs: -2, sm: -3 }, width: 'auto' }}>
                  <Table size="small" sx={{ minWidth: isAdmin ? 960 : 820 }}>
                    <TableHead>
                      <TableRow sx={{ '& th': { color: MUTED, fontWeight: 500, borderColor: RULE } }}>
                        <TableCell sx={{ pl: { xs: 2, sm: 3 } }}>WhatsApp account</TableCell>
                        {isAdmin && <TableCell>Customer</TableCell>}
                        <TableCell>Plan</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Period</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right" sx={{ pr: { xs: 2, sm: 3 } }}>
                          <Box component="span" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Actions</Box>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {history.map((billing) => {
                        const account = accountLabel(billing.whatsappAccount, accounts);
                        const cycle = CYCLES[billing.billingCycle] || CYCLES.monthly;
                        return (
                          <TableRow key={billing._id} hover sx={{ '& td': { borderColor: RULE, py: 1.5, fontVariantNumeric: 'tabular-nums' } }}>
                            <TableCell sx={{ pl: { xs: 2, sm: 3 } }}>
                              <Typography variant="body2" sx={{ color: INK, fontWeight: 500 }}>{account.name}</Typography>
                              <Typography variant="caption" sx={{ color: MUTED }}>{account.phone}</Typography>
                            </TableCell>
                            {isAdmin && (
                              <TableCell>
                                <Typography variant="body2" sx={{ color: INK }}>{billing.user?.company || billing.user?.name || '–'}</Typography>
                                {billing.user?.company && billing.user?.name && (
                                  <Typography variant="caption" sx={{ color: MUTED }}>{billing.user.name}</Typography>
                                )}
                              </TableCell>
                            )}
                            <TableCell>
                              <Typography variant="body2" sx={{ color: INK }}>{planDisplayName(billing.plan)}</Typography>
                              <Typography variant="caption" sx={{ color: MUTED }}>{cycle.label}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ color: INK }}>{formatMoney(billing.amount, billing.currency)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: INK, whiteSpace: 'nowrap' }}>
                                {formatDay(billing.startDate)} to {formatDay(billing.endDate)}
                              </Typography>
                            </TableCell>
                            <TableCell><StatusDot status={billing.status} /></TableCell>
                            <TableCell align="right" sx={{ pr: { xs: 2, sm: 3 }, whiteSpace: 'nowrap' }}>
                              <Button size="small" onClick={() => setInvoiceTarget(billing)} sx={{ textTransform: 'none' }}>Invoices</Button>
                              {billing.status !== 'cancelled' && billing.status !== 'expired' && (
                                <Button size="small" color="error" onClick={() => setCancelTarget(billing)} sx={{ textTransform: 'none' }}>Cancel</Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Section>
          </>
        )}

        {/* Subscribe */}
        <Dialog open={subscribeOpen} onClose={() => !saving && setSubscribeOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ color: INK, fontWeight: 600 }}>Subscribe to a plan</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'grid', gap: 2.5, pt: 1 }}>
              <FormControl fullWidth size="small" required>
                <InputLabel id="sub-account-label">WhatsApp account</InputLabel>
                <Select
                  labelId="sub-account-label"
                  label="WhatsApp account"
                  value={formData.whatsappAccount}
                  onChange={(e) => updateForm('whatsappAccount', e.target.value)}
                >
                  {ownAccounts.map((account) => (
                    <MenuItem key={account._id} value={account._id}>{account.businessName}, {account.phoneNumber}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" required>
                <InputLabel id="sub-plan-label">Plan</InputLabel>
                <Select labelId="sub-plan-label" label="Plan" value={formData.plan} onChange={(e) => updateForm('plan', e.target.value)}>
                  {plans.map((plan) => (
                    <MenuItem key={plan._id} value={plan.name.toLowerCase()}>
                      {plan.name}, {formatMoney(plan.price, plan.currency)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box>
                <Typography variant="body2" sx={{ color: MUTED, mb: 0.75 }}>Billing cycle</Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={formData.billingCycle}
                  onChange={(e, value) => value && updateForm('billingCycle', value)}
                  aria-label="Billing cycle"
                >
                  {Object.entries(CYCLES).map(([value, cycle]) => (
                    <ToggleButton key={value} value={value} sx={{ textTransform: 'none', px: 2 }}>{cycle.label}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="sub-payment-label">Payment method</InputLabel>
                  <Select labelId="sub-payment-label" label="Payment method" value={formData.paymentMethod} onChange={(e) => updateForm('paymentMethod', e.target.value)}>
                    {Object.entries(PAYMENT_METHODS).map(([value, label]) => (
                      <MenuItem key={value} value={value}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  type="date"
                  label="Starts on"
                  value={formData.startDate}
                  onChange={(e) => e.target.value && updateForm('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              <FormControlLabel
                control={<Switch checked={!!formData.autoRenew} onChange={(e) => updateForm('autoRenew', e.target.checked)} />}
                label={<Typography variant="body2" sx={{ color: INK }}>Renew automatically at the end of each cycle</Typography>}
              />

              {selectedPlan && (
                <Box sx={{ bgcolor: '#F6F8FA', borderRadius: 1.5, p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2 }}>
                    <Typography sx={{ fontWeight: 600, color: INK }}>{selectedPlan.name}</Typography>
                    <Typography sx={{ fontWeight: 700, color: INK, fontVariantNumeric: 'tabular-nums' }}>
                      {formatMoney(formData.amount, formData.currency)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: MUTED, mt: 0.5 }}>
                    {formData.startDate && formData.endDate
                      ? `${formatDay(formData.startDate)} to ${formatDay(formData.endDate)}`
                      : ''}
                  </Typography>
                  <Typography variant="body2" sx={{ color: MUTED, mt: 1 }}>
                    {(selectedPlan.messageRateLimit || 0).toLocaleString('en-IN')} messages
                    {FEATURES.filter((f) => selectedPlan.features?.[f.key]).map((f) => `, ${f.label.toLowerCase()}`).join('')}
                    {selectedPlan.maxUsers ? `, ${selectedPlan.maxUsers} team members` : ''}
                    {selectedPlan.fileStorageLimit != null ? `, ${selectedPlan.fileStorageLimit} GB storage` : ''}
                  </Typography>
                </Box>
              )}

              <Typography variant="caption" sx={{ color: MUTED }}>
                The subscription stays pending until the payment is confirmed by our team.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setSubscribeOpen(false)} disabled={saving} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={submitSubscription}
              disabled={saving || !formData.plan || !formData.whatsappAccount}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ textTransform: 'none' }}
            >
              {saving ? 'Subscribing…' : 'Subscribe'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel */}
        <Dialog open={!!cancelTarget} onClose={() => !cancelling && setCancelTarget(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ color: INK, fontWeight: 600 }}>
            Cancel the {cancelTarget ? planDisplayName(cancelTarget.plan) : ''} plan?
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ color: MUTED }}>
              It won't renew, and there are no more charges.
              {cancelTarget?.endDate ? ` You keep access until ${formatDay(cancelTarget.endDate)}.` : ''}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setCancelTarget(null)} disabled={cancelling} sx={{ textTransform: 'none' }}>Keep plan</Button>
            <Button
              variant="contained"
              color="error"
              onClick={confirmCancel}
              disabled={cancelling}
              startIcon={cancelling ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ textTransform: 'none' }}
            >
              {cancelling ? 'Cancelling…' : 'Cancel subscription'}
            </Button>
          </DialogActions>
        </Dialog>

        <InvoiceDialog
          open={!!invoiceTarget}
          onClose={() => setInvoiceTarget(null)}
          billingId={invoiceTarget?._id}
          title={invoiceTarget ? `${planDisplayName(invoiceTarget.plan)} plan, ${accountLabel(invoiceTarget.whatsappAccount, accounts).name}` : ''}
        />

        <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert onClose={() => setError(null)} severity="error" variant="filled" sx={{ width: '100%' }}>{error}</Alert>
        </Snackbar>
        <Snackbar open={!!success} autoHideDuration={5000} onClose={() => setSuccess(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert onClose={() => setSuccess(null)} severity="success" variant="filled" sx={{ width: '100%' }}>{success}</Alert>
        </Snackbar>
      </Container>
    </AppLayout>
  );
};

export default Billing;
